/**
 * Used by mobile website & apps:
 * @see app/Frontend/Library/1.0/app/amazon-pay/AmazonPayButton.js
 * @see app/Frontend/Library/1.0/common/jquery.shopgate-amazon-payments.js
 */
class MinimumOrderValueChecker {
  /**
   * @param {NetworkGateway} networkGateway - communicates with the backend
   * @param {string}         endpoint - endpoint for getting minimum value info
   * @param {Object}         storage - storage used for caching, like localStorage
   */
  constructor(networkGateway, endpoint, storage) {
    this.networkGateway = networkGateway;
    this.endpoint = endpoint;
    this.storage = storage;
    this.storageKey = 'minimumCartValueInfo';
  }

  /**
   * Check if the minimum amount is reached
   * @param {int} quantity      - how many items
   * @param {int} amountDisplay - price of one item, like 100 for 1€
   * @return {Promise}
   */
  test(quantity, amountDisplay) {
    return new Promise((resolve, reject) => {
      this.fetchInfo().then((minimumValueInfo) => {
        const isEnough = minimumValueInfo.value <= quantity * amountDisplay;
        resolve(isEnough);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  /**
   * Fetch the message to be shown to the user when minimum amount is not reached.
   * @return {Promise}
   */
  getMessage() {
    return new Promise((resolve, reject) => {
      this.fetchInfo().then((minimumValueInfo) => {
        resolve(minimumValueInfo.message);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  /**
   * Fetch the minimum value info from localStorage or the backend
   * @return {Promise}
   */
  fetchInfo() {
    return new Promise((resolve, reject) => {
      const infoFromLocalStorage = this.fetchFromLocalStorage();
      if (infoFromLocalStorage) {
        resolve(infoFromLocalStorage);
      } else {
        this.fetchFromBackend().then((infoFromBackend) => {
          resolve(infoFromBackend);
        }).catch((e) => {
          reject(e);
        });
      }
    });
  }

  /**
   * Fetch the minimum value info from localStorage
   * @return {Object|bool} - false on failure
   */
  fetchFromLocalStorage() {
    let fromStorage;
    try {
      fromStorage = this.storage.getItem(this.storageKey);
    } catch (e) {
      // Do nothing.
    }
    if (!fromStorage) {
      return false;
    }

    const parsedInfo = JSON.parse(fromStorage);

    if (
      !parsedInfo.cacheInvalidationTime ||
      MinimumOrderValueChecker.cacheShouldBeInvalidated(parsedInfo.cacheInvalidationTime)
    ) {
      return false;
    }

    if (parsedInfo.value && parsedInfo.message) {
      const minimumValueInfo = {
        value: parsedInfo.value,
        message: parsedInfo.message,
      };
      return minimumValueInfo;
    }
    return false;
  }

  /**
   * @param {string} invalidationTime - UNIX timestamp
   * @return {bool}
   */
  static cacheShouldBeInvalidated(invalidationTime) {
    return new Date(invalidationTime * 1000).getTime() <= new Date().getTime();
  }

  /**
   * Fetch the minimum value info from the backend
   * @return {Promise}
   */
  fetchFromBackend() {
    return new Promise((resolve, reject) => {
      this.networkGateway.get(this.endpoint).then((response) => {
        if (
          response.data === undefined ||
          response.data.value === undefined ||
          response.data.message === undefined ||
          response.data.cacheInvalidationTime === undefined
        ) {
          return reject(`Missing minimum order value data from ${this.endpoint}`);
        }

        const minimumValueInfo = {
          value: response.data.value,
          message: response.data.message,
          cacheInvalidationTime: response.data.cacheInvalidationTime,
        };
        try {
          this.storage.setItem(this.storageKey, JSON.stringify(minimumValueInfo));
        } catch (e) {
          return reject(e);
        }
        return resolve(minimumValueInfo);
      }).catch((e) => {
        reject(e);
      });
    });
  }
}

export default MinimumOrderValueChecker;



// WEBPACK FOOTER //
// ./Library/1.0/common/shop_settings/minimum_order_value/MinimumOrderValueChecker.js